/**
 * Betalingsregelingen — Sprint 18 Feb 2026.
 *
 * Volledige management-pagina voor Payment Arrangements:
 *  - Lijst met filters (alle / concept / awaiting / active / completed / broken)
 *  - "Nieuwe regeling" wizard vanuit klant/factuur
 *  - Detail-drawer met termijnen, Stripe-actie per termijn, register-payment
 *  - KPI-strip
 *
 * REGEL 2: alle bedragen/statuten uit backend endpoints.
 */
import React, { useCallback, useEffect, useMemo, useState } from "react";
import { toast } from "sonner";
import {
  RefreshCw, Search, Plus, Loader2, ExternalLink, Send,
  CheckCircle2, X, AlertTriangle, CreditCard, Calendar, Coins,
  Ban, ClipboardCheck, FilePlus,
} from "lucide-react";
import { adminApi, formatApiError, useAuth } from "../../../context/AuthContext";
import { Button } from "../../ui/button";
import { Input } from "../../ui/input";
import { Badge } from "../../ui/badge";
import { Textarea } from "../../ui/textarea";
import { Label } from "../../ui/label";
import {
  Dialog, DialogContent, DialogHeader, DialogTitle, DialogFooter, DialogDescription,
} from "../../ui/dialog";
import { Sheet, SheetContent, SheetHeader, SheetTitle, SheetDescription } from "../../ui/sheet";
import {
  AdminPageHeader, AdminKpiCard, AdminFilterTabs, AdminSectionCard,
  AdminEmptyState, AdminSkeleton, AdminSearchInput, AdminStatusBadge,
} from "../ui";
import EnterpriseEntityPicker from "../shared/EnterpriseEntityPicker";

// ─── Constants ───────────────────────────────────────────────────
const STATUS_META = {
  concept:         { label: "Concept",              tone: "neutral" },
  awaiting_first:  { label: "Wacht op 1e betaling", tone: "warning" },
  active:          { label: "Actief",               tone: "info" },
  completed:       { label: "Voltooid",             tone: "success" },
  expired:         { label: "Verlopen",             tone: "muted" },
  broken:          { label: "Verbroken",            tone: "danger" },
  cancelled:       { label: "Geannuleerd",          tone: "muted" },
};

const INST_META = {
  open:      { label: "Open",         tone: "neutral" },
  paid:      { label: "Betaald",      tone: "success" },
  overdue:   { label: "Te laat",      tone: "danger" },
  skipped:   { label: "Overgeslagen", tone: "warning" },
  cancelled: { label: "Geannuleerd",  tone: "muted" },
};

const FILTERS = [
  { key: "all", label: "Alle" },
  { key: "concept", label: "Concept" },
  { key: "awaiting_first", label: "Wacht op 1e" },
  { key: "active", label: "Actief" },
  { key: "completed", label: "Voltooid" },
  { key: "broken", label: "Verbroken" },
  { key: "cancelled", label: "Geannuleerd" },
];

const fmtMoney = (v, cur = "EUR") =>
  `${cur} ${Number(v || 0).toLocaleString("nl-NL", {
    minimumFractionDigits: 2, maximumFractionDigits: 2,
  })}`;
const fmtInt = (v) => Number(v || 0).toLocaleString("nl-NL");

// ─── Main component ───────────────────────────────────────────────
export default function BetalingsregelingenSection() {
  const [kpis, setKpis] = useState(null);
  const [items, setItems] = useState([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [filter, setFilter] = useState("all");
  const [search, setSearch] = useState("");
  const [detailId, setDetailId] = useState(null);
  const [showCreate, setShowCreate] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const [kpisR, listR] = await Promise.all([
        adminApi.get("/admin/payment-arrangements/dashboard-kpis"),
        adminApi.get(`/admin/payment-arrangements?status=${filter === "all" ? "" : filter}&search=${encodeURIComponent(search)}&limit=100`),
      ]);
      setKpis(kpisR.data);
      setItems(listR.data.items || []);
      setTotal(listR.data.total || 0);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [filter, search]);

  useEffect(() => { load(); }, [load]);

  return (
    <div className="space-y-5" data-testid="regelingen-section">
      {/* Header */}
      <AdminPageHeader
        icon={ClipboardCheck}
        title="Betalingsregelingen"
        subtitle="Overzicht en beheer van alle actieve betalingsregelingen"
        actions={
          <>
            <Button
              variant="outline"
              onClick={load}
              disabled={loading}
              className="rounded-full border-slate-200 hover:border-brand-500 hover:text-brand-500"
              data-testid="regelingen-refresh"
            >
              {loading ? <Loader2 className="w-4 h-4 animate-spin mr-1.5" /> : <RefreshCw className="w-4 h-4 mr-1.5" />}
              Vernieuwen
            </Button>
            <Button
              onClick={() => setShowCreate(true)}
              className="rounded-full bg-brand-900 hover:bg-brand-700 text-white shadow-brand-soft"
              data-testid="regelingen-new"
            >
              <Plus className="w-4 h-4 mr-1.5" /> Nieuwe regeling
            </Button>
          </>
        }
      />

      {/* KPI strip */}
      <div className="grid grid-cols-2 sm:grid-cols-4 lg:grid-cols-7 gap-3" data-testid="regelingen-kpis">
        <AdminKpiCard label="Actief"              value={fmtInt(kpis?.active_arrangements)}            icon={ClipboardCheck} tone="info"    testid="kpi-active" />
        <AdminKpiCard label="Openstaand"          value={fmtMoney(kpis?.outstanding_in_arrangements)}  icon={Coins}          tone="brand"   testid="kpi-outstanding" />
        <AdminKpiCard label="Termijn vandaag"     value={fmtInt(kpis?.installments_due_today)}         icon={Calendar}       tone="amber"   testid="kpi-today" />
        <AdminKpiCard label="Termijn deze week"   value={fmtInt(kpis?.installments_due_this_week)}     icon={Calendar}       tone="slate"   testid="kpi-week" />
        <AdminKpiCard label="Regeling met risico" value={fmtInt(kpis?.arrangements_at_risk)}           icon={AlertTriangle}  tone="orange"  testid="kpi-risk" />
        <AdminKpiCard label="Verlopen"            value={fmtInt(kpis?.expired_arrangements)}           icon={X}              tone="slate"   testid="kpi-expired" />
        <AdminKpiCard label="Verbroken"           value={fmtInt(kpis?.broken_arrangements)}            icon={Ban}            tone="red"     testid="kpi-broken" />
      </div>

      {/* Filter + search */}
      <AdminSectionCard padded={false} testid="regelingen-filter-card">
        <div className="px-4 py-3 flex items-center gap-3 flex-wrap">
          <AdminFilterTabs
            items={FILTERS}
            value={filter}
            onChange={setFilter}
            testidPrefix="regelingen-filter"
          />
          <div className="ml-auto flex items-center gap-3">
            <AdminSearchInput
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              placeholder="Zoek nr, klant, factuur…"
              testid="regelingen-search"
            />
            <div className="text-xs text-ink-500 whitespace-nowrap font-medium">{total} regelingen</div>
          </div>
        </div>
      </AdminSectionCard>

      {/* Table */}
      <ArrangementList
        items={items} loading={loading}
        onSelect={setDetailId}
      />

      {/* Detail Sheet */}
      {detailId && (
        <ArrangementDetailDrawer
          arrangementId={detailId}
          onClose={() => setDetailId(null)}
          onChanged={load}
        />
      )}

      {/* Create wizard */}
      {showCreate && (
        <CreateArrangementDialog
          open={showCreate}
          onClose={() => setShowCreate(false)}
          onCreated={(id) => { setShowCreate(false); setDetailId(id); load(); }}
        />
      )}
    </div>
  );
}

// (Legacy KpiCard verwijderd — vervangen door AdminKpiCard uit /admin/ui.)

// ─── Table ────────────────────────────────────────────────────────
function ArrangementList({ items, loading, onSelect }) {
  if (loading) {
    return <AdminSkeleton variant="table" rows={5} cols={6} />;
  }
  if (!items?.length) {
    return (
      <AdminEmptyState
        icon={FilePlus}
        title="Nog geen betalingsregelingen"
        description={'Klik op "Nieuwe regeling" om de eerste regeling voor een klant aan te maken.'}
        testid="regelingen-empty"
      />
    );
  }
  return (
    <AdminSectionCard padded={false} testid="regelingen-list-card">
      <div className="overflow-hidden" data-testid="regelingen-list">
        <div className="grid grid-cols-12 gap-2 px-5 py-3 bg-slate-50/80 border-b border-slate-200 text-[10px] uppercase tracking-[0.14em] text-ink-500 font-chivo font-bold">
          <div className="col-span-2">Regelingnr</div>
          <div className="col-span-3">Klant</div>
          <div className="col-span-2">Status</div>
          <div className="col-span-1 text-right">Termijnen</div>
          <div className="col-span-2 text-right">Totaal</div>
          <div className="col-span-2 text-right">Resterend</div>
        </div>
        {items.map((it) => {
          const meta = STATUS_META[it.status] || { label: it.status, tone: "neutral" };
          return (
            <button
              key={it.id}
              type="button"
              onClick={() => onSelect(it.id)}
              className="grid grid-cols-12 gap-2 px-5 py-3 border-b border-slate-100 last:border-b-0 hover:bg-brand-50/40 text-sm w-full text-left items-center transition-colors"
              data-testid={`regelingen-row-${it.arrangement_number}`}
            >
              <div className="col-span-2 font-mono-num tabular-nums font-semibold text-brand-900">{it.arrangement_number}</div>
              <div className="col-span-3 min-w-0">
                <div className="font-semibold text-brand-900 truncate">{it.customer_name}</div>
                <div className="text-[11px] text-ink-500 truncate">{it.customer_email}</div>
              </div>
              <div className="col-span-2">
                <AdminStatusBadge tone={meta.tone} label={meta.label} size="xs" />
              </div>
              <div className="col-span-1 text-right text-ink-500">{it.invoice_numbers?.length || 0} fact.</div>
              <div className="col-span-2 text-right font-mono-num tabular-nums text-brand-900 font-semibold">{fmtMoney(it.agreed_amount, it.currency)}</div>
              <div className="col-span-2 text-right font-mono-num tabular-nums text-brand-900 font-bold">{fmtMoney(it.remaining_amount, it.currency)}</div>
            </button>
          );
        })}
      </div>
    </AdminSectionCard>
  );
}

// ─── Detail drawer ────────────────────────────────────────────────
function ArrangementDetailDrawer({ arrangementId, onClose, onChanged }) {
  const [data, setData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [busy, setBusy] = useState(false);

  const load = useCallback(async () => {
    setLoading(true);
    try {
      const r = await adminApi.get(`/admin/payment-arrangements/${arrangementId}`);
      setData(r.data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  }, [arrangementId]);

  useEffect(() => { load(); }, [load]);

  const doAction = async (path, body, msg) => {
    setBusy(true);
    try {
      await adminApi.post(`/admin/payment-arrangements/${arrangementId}${path}`, body || {});
      toast.success(msg);
      await load();
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const arr = data?.arrangement;

  return (
    <Sheet open={!!arrangementId} onOpenChange={(v) => { if (!v) onClose(); }}>
      <SheetContent className="w-[95vw] sm:max-w-2xl overflow-y-auto p-0" data-testid="regeling-drawer">
        <SheetHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50 sticky top-0 z-10">
          <SheetTitle>
            {loading ? "Laden…" : arr?.arrangement_number || "Regeling"}
          </SheetTitle>
          <SheetDescription className="text-xs text-slate-600">
            {arr && (
              <span>
                Klant {arr.customer_name} · Totaal {fmtMoney(arr.total_amount, arr.currency)} · Resterend {fmtMoney(arr.remaining_amount, arr.currency)}
              </span>
            )}
          </SheetDescription>
        </SheetHeader>
        {loading || !data ? (
          <div className="p-10 text-center text-slate-400"><Loader2 className="w-5 h-5 animate-spin inline mr-2" />Laden…</div>
        ) : (
          <div className="p-5 space-y-5">
            {/* Status + acties */}
            <div className="flex items-center justify-between gap-3 flex-wrap">
              <AdminStatusBadge
                tone={STATUS_META[arr.status]?.tone || "neutral"}
                label={STATUS_META[arr.status]?.label || arr.status}
                size="md"
              />
              <div className="flex items-center gap-1 flex-wrap">
                {arr.status === "concept" && (
                  <Button size="sm" onClick={() => doAction("/approve", {}, "Regeling goedgekeurd")} disabled={busy} className="bg-emerald-600 hover:bg-emerald-700 text-white" data-testid="regeling-approve">
                    <CheckCircle2 className="w-3 h-3 mr-1" /> Goedkeuren
                  </Button>
                )}
                {["awaiting_first", "active"].includes(arr.status) && (
                  <Button size="sm" variant="outline" onClick={() => {
                    const reason = window.prompt("Reden voor verbreken?", "Klant volgt regeling niet") || "";
                    doAction("/break", { reason }, "Regeling verbroken");
                  }} disabled={busy} className="border-red-200 text-red-700 hover:bg-red-50" data-testid="regeling-break">
                    <Ban className="w-3 h-3 mr-1" /> Verbreken
                  </Button>
                )}
                {!["cancelled", "completed", "broken", "expired"].includes(arr.status) && (
                  <Button size="sm" variant="outline" onClick={() => {
                    const reason = window.prompt("Reden voor annuleren?", "") || "";
                    doAction("/cancel", { reason }, "Regeling geannuleerd");
                  }} disabled={busy} data-testid="regeling-cancel">
                    <X className="w-3 h-3 mr-1" /> Annuleren
                  </Button>
                )}
              </div>
            </div>

            {/* Termijnen */}
            <section>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                Termijnen ({data.installments?.length || 0})
              </div>
              <div className="border border-slate-200 rounded-md overflow-hidden" data-testid="regeling-installments">
                {(data.installments || []).map((i) => (
                  <InstallmentRow
                    key={i.id}
                    inst={i}
                    arrangementId={arr.id}
                    arrangementStatus={arr.status}
                    onChanged={() => { load(); onChanged?.(); }}
                  />
                ))}
              </div>
            </section>

            {/* Gekoppelde facturen */}
            <section>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                Gekoppelde facturen ({data.invoices?.length || 0})
              </div>
              <div className="space-y-1">
                {(data.invoices || []).map((inv) => (
                  <div key={inv.id} className="text-xs text-slate-700 border border-slate-200 rounded px-3 py-2 bg-slate-50 flex items-center gap-2">
                    <span className="font-mono font-medium">{inv.invoice_number}</span>
                    <span className="text-slate-500">{inv.invoice_date}</span>
                    <span className="text-slate-500 uppercase text-[10px]">{inv.financial_status || inv.status}</span>
                    <span className="ml-auto font-semibold">{fmtMoney(inv.amount_due || inv.total_incl_vat, inv.currency)}</span>
                  </div>
                ))}
              </div>
            </section>

            {/* Timeline events */}
            <section>
              <div className="text-xs font-semibold uppercase tracking-wide text-slate-600 mb-2">
                Historie ({data.events?.length || 0})
              </div>
              <div className="space-y-1 max-h-64 overflow-y-auto" data-testid="regeling-events">
                {(data.events || []).map((e) => (
                  <div key={e.id} className="text-xs border border-slate-200 rounded p-2 flex items-start gap-2">
                    <span className="text-slate-500 whitespace-nowrap">{(e.ts || "").slice(0, 16).replace("T", " ")}</span>
                    <span className="flex-1 text-slate-800">{e.description}</span>
                    <Badge variant="outline" className="text-[9px]">{e.actor}</Badge>
                  </div>
                ))}
              </div>
            </section>
          </div>
        )}
      </SheetContent>
    </Sheet>
  );
}

// ─── Installment row ─────────────────────────────────────────────
function InstallmentRow({ inst, arrangementId, arrangementStatus, onChanged }) {
  const [busy, setBusy] = useState(false);
  const meta = INST_META[inst.status] || INST_META.open;

  const createStripe = async () => {
    setBusy(true);
    try {
      await adminApi.post(
        `/admin/payment-arrangements/${arrangementId}/installments/${inst.id}/stripe-checkout`,
      );
      toast.success("Stripe-betaallink aangemaakt");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const registerManual = async () => {
    const amt = window.prompt(`Bedrag betaald voor termijn ${inst.sequence_number}?`, String(inst.amount_remaining ?? inst.amount));
    if (!amt) return;
    setBusy(true);
    try {
      await adminApi.post(
        `/admin/payment-arrangements/${arrangementId}/installments/${inst.id}/register-payment`,
        { amount: Number(amt), payment_date: new Date().toISOString().slice(0, 10), payment_method: "bank" },
      );
      toast.success("Betaling geregistreerd");
      onChanged?.();
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  const canAct = arrangementStatus === "awaiting_first" || arrangementStatus === "active";
  const remaining = Number(inst.amount_remaining ?? inst.amount);

  return (
    <div className="flex items-center gap-3 px-3 py-2 border-b border-slate-100 last:border-b-0 text-sm" data-testid={`installment-row-${inst.sequence_number}`}>
      <div className="w-8 text-center text-ink-500 font-mono-num tabular-nums">{inst.sequence_number}</div>
      <div className="flex-1 min-w-0">
        <div className="font-semibold text-brand-900">{fmtMoney(inst.amount, inst.currency)}</div>
        <div className="text-[11px] text-ink-500">
          Vervalt {inst.due_date}
          {inst.amount_paid > 0 && ` · Betaald ${fmtMoney(inst.amount_paid, inst.currency)}`}
        </div>
      </div>
      <span className="ml-auto"><AdminStatusBadge tone={meta.tone} label={meta.label} size="xs" /></span>
      {canAct && inst.status !== "paid" && inst.status !== "cancelled" && remaining > 0 && (
        <>
          {inst.stripe_payment_url ? (
            <a href={inst.stripe_payment_url} target="_blank" rel="noopener noreferrer" className="text-[11px] px-2 py-1 border border-slate-200 hover:bg-slate-100 rounded flex items-center gap-1" data-testid={`installment-open-stripe-${inst.sequence_number}`}>
              <ExternalLink className="w-3 h-3" /> Open Stripe
            </a>
          ) : (
            <button type="button" onClick={createStripe} disabled={busy} className="text-[11px] px-2 py-1 border border-slate-200 hover:bg-slate-100 rounded" data-testid={`installment-create-stripe-${inst.sequence_number}`}>
              <CreditCard className="w-3 h-3 inline mr-1" /> Stripe-link
            </button>
          )}
          <button type="button" onClick={registerManual} disabled={busy} className="text-[11px] px-2 py-1 border border-emerald-200 text-emerald-700 hover:bg-emerald-50 rounded" data-testid={`installment-register-${inst.sequence_number}`}>
            <Coins className="w-3 h-3 inline mr-1" /> Registreer
          </button>
        </>
      )}
    </div>
  );
}

// ─── Create wizard ────────────────────────────────────────────────
function CreateArrangementDialog({ open, onClose, onCreated }) {
  // Sprint A1 (22 Feb 2026) — vervangen door EnterpriseEntityPicker.
  // De aparte customerSearch state + useEffect zijn verplaatst naar de
  // universele component in `/components/admin/shared/EnterpriseEntityPicker`.
  // Backend-contract (adminApi.get "/admin/customers?q=...") blijft
  // ongewijzigd — de picker roept nu `/admin/entity-search` aan wat via
  // dezelfde `search_text` matrix filtert (naam, bedrijfsnaam, e-mail,
  // telefoon, adres, postcode, KvK, BTW).
  const [selectedCustomer, setSelectedCustomer] = useState(null);
  const [outstandingInvoices, setOutstandingInvoices] = useState([]);
  const [selectedInvoices, setSelectedInvoices] = useState(new Set());
  const [installments, setInstallments] = useState([{ due_date: "", amount: "" }]);
  const [busy, setBusy] = useState(false);
  const [notes, setNotes] = useState("");

  useEffect(() => {
    if (!selectedCustomer) return;
    (async () => {
      try {
        const r = await adminApi.get(`/admin/customers/${selectedCustomer.id}/outstanding-invoices`);
        const eligible = (r.data.invoices || []).filter(
          (i) => ["verzonden", "open", "deels_betaald"].includes(i.financial_status) && i.amount_due > 0,
        );
        setOutstandingInvoices(eligible);
        setSelectedInvoices(new Set(eligible.map((i) => i.id)));
      } catch (e) {
        toast.error(formatApiError(e));
      }
    })();
  }, [selectedCustomer]);

  const totalOpen = useMemo(
    () => outstandingInvoices
      .filter((i) => selectedInvoices.has(i.id))
      .reduce((s, i) => s + Number(i.amount_due || 0), 0),
    [outstandingInvoices, selectedInvoices],
  );

  const totalInstallments = useMemo(
    () => installments.reduce((s, i) => s + Number(i.amount || 0), 0),
    [installments],
  );
  const totalMatches = Math.abs(totalInstallments - totalOpen) < 0.02;

  const addInstallment = () => {
    setInstallments((s) => [...s, { due_date: "", amount: "" }]);
  };
  const updateInstallment = (idx, field, value) => {
    setInstallments((s) => s.map((v, i) => (i === idx ? { ...v, [field]: value } : v)));
  };
  const removeInstallment = (idx) => {
    setInstallments((s) => s.filter((_, i) => i !== idx));
  };
  const splitEqual = (n) => {
    const per = Math.round(totalOpen / n * 100) / 100;
    const last = Math.round((totalOpen - per * (n - 1)) * 100) / 100;
    const rows = [];
    const today = new Date();
    for (let i = 0; i < n; i++) {
      const d = new Date(today); d.setMonth(d.getMonth() + i + 1);
      rows.push({ due_date: d.toISOString().slice(0, 10), amount: (i === n - 1 ? last : per).toFixed(2) });
    }
    setInstallments(rows);
  };

  const submit = async () => {
    if (!selectedCustomer) { toast.error("Kies eerst een klant"); return; }
    if (selectedInvoices.size === 0) { toast.error("Selecteer minstens 1 factuur"); return; }
    if (!totalMatches) { toast.error("Som van termijnen moet gelijk zijn aan het openstaand bedrag"); return; }
    setBusy(true);
    try {
      const r = await adminApi.post("/admin/payment-arrangements", {
        customer_id: selectedCustomer.id,
        invoice_ids: Array.from(selectedInvoices),
        installments: installments.map((i) => ({ due_date: i.due_date, amount: Number(i.amount) })),
        notes: notes || null,
      });
      toast.success(`Regeling ${r.data.arrangement.arrangement_number} aangemaakt`);
      onCreated(r.data.arrangement.id);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setBusy(false);
    }
  };

  return (
    <Dialog open={open} onOpenChange={(v) => { if (!v) onClose(); }}>
      <DialogContent className="max-w-3xl w-[95vw] max-h-[92vh] flex flex-col p-0 overflow-hidden rounded-md" data-testid="regeling-create-dialog">
        <DialogHeader className="px-5 py-3 border-b border-slate-200 bg-slate-50">
          <DialogTitle>Nieuwe betalingsregeling</DialogTitle>
          <DialogDescription className="sr-only">Kies klant en facturen, verdeel over termijnen.</DialogDescription>
        </DialogHeader>
        <div className="flex-1 overflow-y-auto p-5 space-y-4">
          {/* Step 1: klant */}
          <section>
            <Label className="text-xs uppercase text-slate-500 mb-1 block">1. Klant</Label>
            {selectedCustomer ? (
              <div className="flex items-center justify-between gap-2 bg-emerald-50 border border-emerald-200 rounded p-2">
                <div className="min-w-0">
                  <div className="font-medium text-emerald-900 truncate">
                    {selectedCustomer.primary_text
                      || selectedCustomer.display_name
                      || [selectedCustomer.first_name, selectedCustomer.last_name].filter(Boolean).join(" ")
                      || selectedCustomer.company_name
                      || selectedCustomer.email
                      || "Klant"}
                  </div>
                  <div className="text-xs text-emerald-700 truncate">
                    {selectedCustomer.secondary_text
                      || [selectedCustomer.company_name, selectedCustomer.email, selectedCustomer.phone]
                        .filter(Boolean).join(" · ")}
                  </div>
                </div>
                <button type="button" onClick={() => { setSelectedCustomer(null); setOutstandingInvoices([]); setSelectedInvoices(new Set()); }} className="text-xs text-red-700 hover:underline" data-testid="regeling-clear-customer">
                  Wijzig
                </button>
              </div>
            ) : (
              <EnterpriseEntityPicker
                entityType="customer"
                mode="inline"
                testIdPrefix="regeling-customer"
                onSelect={(item) => {
                  // EnterpriseEntityPicker retourneert een SearchResult:
                  // {id, entity_type, primary_text, secondary_text,
                  //  metadata: {email, first_name, last_name, company_name,
                  //  phone, city, country}, ...}
                  // We normaliseren naar het "customer" shape dat de rest
                  // van de wizard verwacht (id, first_name, last_name,
                  // email, phone, company_name).
                  setSelectedCustomer({
                    id: item.id,
                    primary_text: item.primary_text,
                    secondary_text: item.secondary_text,
                    ...(item.metadata || {}),
                  });
                }}
              />
            )}
          </section>

          {selectedCustomer && (
            <section>
              <Label className="text-xs uppercase text-slate-500 mb-1 block">2. Facturen ({outstandingInvoices.length} openstaand)</Label>
              {outstandingInvoices.length === 0 ? (
                <div className="text-sm text-slate-500 italic p-3 bg-slate-50 rounded">Geen openstaande facturen voor deze klant.</div>
              ) : (
                <div className="border border-slate-200 rounded">
                  {outstandingInvoices.map((inv) => (
                    <label key={inv.id} className="flex items-center gap-2 px-3 py-2 border-b border-slate-100 last:border-b-0 hover:bg-slate-50 cursor-pointer text-sm">
                      <input
                        type="checkbox"
                        checked={selectedInvoices.has(inv.id)}
                        onChange={() => {
                          const s = new Set(selectedInvoices);
                          if (s.has(inv.id)) s.delete(inv.id); else s.add(inv.id);
                          setSelectedInvoices(s);
                        }}
                        data-testid={`regeling-invoice-check-${inv.invoice_number}`}
                      />
                      <span className="font-mono">{inv.invoice_number}</span>
                      <span className="text-slate-500">{inv.invoice_date}</span>
                      <span className="ml-auto font-semibold">{fmtMoney(inv.amount_due, inv.currency)}</span>
                    </label>
                  ))}
                </div>
              )}
              <div className="mt-2 text-right text-sm">
                <span className="text-slate-500">Openstaand geselecteerd: </span>
                <span className="font-semibold">{fmtMoney(totalOpen)}</span>
              </div>
            </section>
          )}

          {selectedCustomer && selectedInvoices.size > 0 && (
            <section>
              <div className="flex items-center justify-between mb-1">
                <Label className="text-xs uppercase text-slate-500">3. Termijnen</Label>
                <div className="flex items-center gap-1">
                  {[2, 3, 4, 6].map((n) => (
                    <button
                      key={n}
                      type="button"
                      onClick={() => splitEqual(n)}
                      className="text-[10px] px-2 py-0.5 border border-slate-200 rounded hover:bg-slate-100"
                      data-testid={`regeling-split-${n}`}
                    >
                      {n}x gelijk
                    </button>
                  ))}
                </div>
              </div>
              <div className="space-y-1">
                {installments.map((inst, idx) => (
                  <div key={idx} className="flex items-center gap-2" data-testid={`regeling-installment-${idx}`}>
                    <span className="text-xs text-slate-500 w-8 text-center">#{idx + 1}</span>
                    <Input
                      type="date"
                      value={inst.due_date}
                      onChange={(e) => updateInstallment(idx, "due_date", e.target.value)}
                      className="h-8 flex-1"
                    />
                    <Input
                      type="number"
                      step="0.01"
                      value={inst.amount}
                      onChange={(e) => updateInstallment(idx, "amount", e.target.value)}
                      placeholder="Bedrag"
                      className="h-8 w-32"
                    />
                    <button type="button" onClick={() => removeInstallment(idx)} className="text-slate-400 hover:text-red-600 text-xs px-1">
                      <X className="w-3 h-3" />
                    </button>
                  </div>
                ))}
                <button type="button" onClick={addInstallment} className="text-xs text-blue-700 hover:underline flex items-center gap-1 mt-1">
                  <Plus className="w-3 h-3" /> Termijn toevoegen
                </button>
              </div>
              <div className="mt-2 text-right text-xs">
                Som termijnen: <span className="font-semibold">{fmtMoney(totalInstallments)}</span>
                {" — "}
                {totalMatches ? (
                  <span className="text-emerald-700">✓ komt overeen met openstaand bedrag</span>
                ) : (
                  <span className="text-red-700">verschil {fmtMoney(totalOpen - totalInstallments)}</span>
                )}
              </div>
            </section>
          )}

          <section>
            <Label className="text-xs uppercase text-slate-500 mb-1 block">Interne notitie (optioneel)</Label>
            <Textarea value={notes} onChange={(e) => setNotes(e.target.value)} rows={2} className="text-sm" data-testid="regeling-notes" />
          </section>
        </div>
        <DialogFooter className="px-5 py-3 border-t border-slate-200 bg-slate-50">
          <Button variant="outline" onClick={onClose} disabled={busy}>Annuleren</Button>
          <Button
            onClick={submit}
            disabled={busy || !selectedCustomer || selectedInvoices.size === 0 || !totalMatches}
            className="rounded-full bg-brand-900 hover:bg-brand-700 text-white shadow-brand-soft"
            data-testid="regeling-create-submit"
          >
            {busy ? <Loader2 className="w-4 h-4 animate-spin mr-1" /> : <FilePlus className="w-4 h-4 mr-1" />}
            Regeling aanmaken
          </Button>
        </DialogFooter>
      </DialogContent>
    </Dialog>
  );
}
